import React, { useEffect, useState, useRef } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withRoomClientContext } from '../lib/RoomClientContext';
import { useShouldTestNetwork } from '../hooks/useShouldTestNetwork';
import LayoutContainer from '../components/LayoutContainer';
import ContactSupport from '../components/ContactSupport';
import Devices from '../components/Devices';
import VideoFrame from '../components/VideoFrame';
import ConfrereButton from '../components/ConfrereButton';
import ErrorDialog from '../components/Errors/ErrorDialog';
import { NetworkStatusDialog, NetworkTest } from '../components/Network';
import ActionButtonText from '../components/ActionButtonText';
import { clearEncounteredErrors } from '../store/slices/statusSlice';
import { clearScenario } from '../store/slices/scenarioSlice';

function Hero({ roomClient }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { onCompletion } = useSelector((state) => state.scenario);
  const { videoDeviceId, microphoneDeviceId } = useSelector(
    (state) => state.device
  );
  const { video, microphone, speaker, network, connection } = useSelector(
    (state) => state.status
  );
  const { videoinputDevices } = useSelector((state) => state.device);
  const showActionButton = useSelector(
    (state) => state.content.showActionButton
  );
  const reloadingRef = useRef(false);
  const [videoStream, setVideoStream] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const { doNetworkTest } = useShouldTestNetwork();

  const hasError = video || microphone || speaker || network; // Check if any error exists

  useEffect(() => {
    if (!videoDeviceId) return;

    (async () => {
      const stream = await roomClient.streamHandler.getVideoStream(
        videoDeviceId
      );
      roomClient.replaceStreamTrack({ stream, type: 'video' });
      setVideoStream(stream);
    })();

    return () => {
      if (videoStream) {
        roomClient.streamHandler.stopStream(videoStream);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoDeviceId]);

  useEffect(() => {
    if (!microphoneDeviceId) return;

    (async () => {
      const stream = await roomClient.streamHandler.getAudioStream(
        microphoneDeviceId
      );
      roomClient.replaceStreamTrack({ stream, type: 'audio' });
      setAudioStream(stream);
    })();

    return () => {
      if (audioStream) {
        roomClient.streamHandler.stopStream(audioStream);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [microphoneDeviceId]);

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      if (!reloadingRef.current) dispatch(clearEncounteredErrors());
    });
  }, [dispatch]);

  const renderActions = () => {
    return (
      <>
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mt: '1rem',
          }}
        >
          {showActionButton && (
            <ConfrereButton
              size="small"
              sx={{ margin: '8px 16px' }}
              onClick={() => {
                dispatch(clearEncounteredErrors());
                dispatch(clearScenario());
                onCompletion();
              }}
            >
              <ActionButtonText />
            </ConfrereButton>
          )}
        </Grid>
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mb: '1rem',
          }}
        >
          <ContactSupport />
        </Grid>
      </>
    );
  };

  return (
    <>
      <LayoutContainer header>
        <Grid alignItems="center">
          <Grid item sx={{ width: '100%' }}>
            <Typography align="center" sx={{ fontSize: '16px' }}>
              {t('Test your devices')}
            </Typography>
          </Grid>
          {videoinputDevices.length > 0 && (
            <Grid item sx={{ width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  my: '16px',
                }}
              >
                <VideoFrame />
              </Box>
            </Grid>
          )}
          <Grid
            item
            sx={{
              width: '100%',
            }}
          >
            <Devices streamHandler={roomClient.streamHandler} />
          </Grid>
          {doNetworkTest && <NetworkTest roomClient={roomClient} />}
        </Grid>
        {!connection && renderActions()}
      </LayoutContainer>
      <ErrorDialog open={!!hasError} reloadingRef={reloadingRef} />
      <NetworkStatusDialog>{renderActions()}</NetworkStatusDialog>
    </>
  );
}

const HeroWrapper = ({ roomClient }) => {
  return <Hero roomClient={roomClient} />;
};

export default withRoomClientContext(HeroWrapper);
